
import { Language } from './types';

export const TRANSLATIONS: Record<Language, Record<string, string>> = {
    en: {
        // Header & Navigation
        settings: "Settings",
        stats: "Statistics",
        help: "Help",
        home_subtitle: "Guess the Titan Characters",
        choose_challenge: "Choose Your Challenge",

        // Game Modes
        classic: "Classic",
        classic_desc: "Guess the character from attributes",
        quote: "Quote",
        quote_desc: "Identify the speaker",
        image: "Image",
        image_desc: "Recognize the character",

        // Game Interface
        search_placeholder: "Search for a character...",
        archive_closed: "Survey Complete",
        record: "Titan Record",
        discovery: "Identity Confirmed",
        it_was: "It was",

        // Column Headers
        col_character: "Character",
        col_gender: "Gender",
        col_arc: "Arc",
        col_affiliation: "Regiment",
        col_species: "Species",
        col_height: "Height",
        col_status: "Status",

        // Stats
        records: "Records",
        played: "Played",
        win_rate: "Win Rate",
        streak: "Streak",
        next_entry: "Next Character In",

        // Help Section
        guide: "Survey Corps Guide",
        how_to: "How to Play",
        help_intro: "Identify the hidden Attack on Titan character.",
        indicators: "Indicators",
        exact_match: "Exact Match",
        partial_match: "Partial Match (adjacent arc)",
        incorrect: "Incorrect",
        chronology: "Chronology",
        chronology_desc: "Arrows indicate if the character first appeared in an earlier (↓) or later (↑) arc.",
        language: "Language",
        hint: "Hint",
        unlocks_in: "Unlocks in",
        yesterday_character: "Yesterday's character #{number} was {name}",

        // Victory & Share
        share_results: "Share Results",
        share_twitter: "X / Twitter",
        share_whatsapp: "WhatsApp",
        copied: "Copied!",
        victory_heading: "Shinzou wo Sasageyo!",
        next_character: "Next Character",

        // Arc Names
        arc_Fall_of_Shiganshina: "Fall of Shiganshina",
        arc_Training: "Training",
        arc_Trost: "Trost",
        arc_Female_Titan: "Female Titan",
        arc_Clash_of_Titans: "Clash of Titans",
        arc_Uprising: "Uprising",
        arc_Return_to_Shiganshina: "Return to Shiganshina",
        arc_Marley: "Marley",
        arc_War_for_Paradis: "War for Paradis",

        // Rules
        rule_classic_header: "Classic Mode",
        rule_classic_text: "Guess the character from attributes:",
        rule_classic_gender: "Gender: Exact match.",
        rule_classic_arc: "Arc: Green if exact. Yellow if adjacent. Arrows (↑/↓) show timeline direction.",
        rule_classic_affiliation: "Regiment: Exact match.",
        rule_classic_species: "Species: Exact match.",
        rule_classic_height: "Height: Exact match.",
        rule_classic_status: "Status: Exact match.",
        rule_quote_header: "Quote Mode",
        rule_quote_text: "Identify the character who said the quote.",
        rule_image_header: "Image Mode",
        rule_image_text: "Identify the character from their image.",
        rule_guesses: "You have unlimited guesses.",

        // Footer & Legal
        footer_privacy: "Privacy Policy",
        footer_about: "About",
        footer_contact: "Contact",
        back_to_game: "Back to Survey",

        // Privacy Page
        privacy_title: "Privacy Policy",
        privacy_intro: "At AOTdle, we prioritize your privacy. This policy outlines how we handle information.",
        privacy_point_1: "No Personal Data Collection: We do not require user accounts, logins, or emails. You are completely anonymous while playing.",
        privacy_point_2: "Cookies & Consent: We use LocalStorage for language, daily progress, and consent preferences. We do not use Google Analytics.",
        privacy_point_3: "Advertising: We use Google AdSense. Ad settings are managed through Google Consent Mode based on your consent choice.",

        // About Page
        about_title: "About AOTdle",
        about_text: "AOTdle is a daily guessing game for Attack on Titan fans. Inspired by Wordle, our goal is to make testing your AOT knowledge engaging and fun. New challenges are released every day at midnight. This is a fan-made project and is not affiliated with or endorsed by the creators of Attack on Titan.",

        // Contact Page
        contact_title: "Contact Us",
        contact_text: "Have a suggestion for a character or found a bug?",
        contact_email: "Contact us at: hello@aotdle.com",

        // Settings
        setting_high_contrast: "High Contrast Mode",
        setting_high_contrast_desc: "Increases color visibility.",
        setting_reset: "Reset Data",
        setting_reset_desc: "Clears stats and history.",
        setting_reset_confirm: "Are you sure?",
        feedback: "Feedback",

        // About Page extended
        about_features: "Game Features",
        about_feature_1: "67+ Attack on Titan characters",
        about_feature_2: "3 game modes: Classic, Quote, and Image",
        about_feature_3: "Available in English, Turkish and Spanish",
        about_feature_4: "New challenges every day at midnight",
        about_feature_5: "Share your results with friends",
        about_credits: "Credits",
        about_credits_text: "Attack on Titan (Shingeki no Kyojin) is created by Hajime Isayama. This is a fan-made project for entertainment purposes only. All character images and names are property of their respective owners.",

        // Contact Page extended
        send_email: "Send Email",
        follow_us: "Follow Us",

        // Settings extended
        reset_button: "Reset",
        max_streak: "Max",

        // Value translations - Affiliations
        affiliation_Scout_Regiment: "Scout Regiment",
        affiliation_Garrison: "Garrison",
        affiliation_Military_Police: "Military Police",
        affiliation_Warriors: "Warriors",
        affiliation_Yeagerists: "Yeagerists",
        affiliation_Civilians: "Civilians",
        affiliation_Marleyan_Military: "Marleyan Military",

        // Value translations - Species
        species_Human: "Human",
        species_Titan_Shifter: "Titan Shifter",

        // Value translations - Genders
        gender_Male: "Male",
        gender_Female: "Female",

        // Value translations - Height
        height_Short: "Short",
        height_Average: "Average",
        height_Tall: "Tall",

        // Value translations - Status
        status_Alive: "Alive",
        status_Deceased: "Deceased",

        // Cookie Consent
        cookie_title: "Cookie Notice",
        cookie_message: "We use storage and consent signals for core features and AdSense ad settings. You can accept or decline ad-related consent.",
        cookie_accept: "Accept All",
        cookie_decline: "Decline",

        // Landing Page Stats
        landing_subtitle: "Test your Attack on Titan knowledge",
        stat_characters: "67+ Characters",
        stat_arcs: "9 Story Arcs",
        stat_daily: "Daily Challenges",
        footer_disclaimer: "Fan-made • Not affiliated with Attack on Titan",
        footer_copyright: "© {year} AOTdle • Fan-made project • Not affiliated with Attack on Titan",
        footer_description: "AOTdle is a free daily guessing game for Attack on Titan fans. Test your knowledge by identifying characters from clues, quotes, and images. New challenges available every day at midnight.",

        // Autocomplete
        no_results: "No characters found",
        results_found: "characters found",
        character_results: "Character search results",
        search_instructions: "Type to search characters. Use arrow keys to navigate results, Enter to select.",

        // Share
        guess_singular: "guess",
        guess_plural: "guesses",

        // Navigation
        nav_characters: "Characters",
        nav_guide: "How to Play",
        nav_trivia: "Trivia",

        // Character Gallery
        characters_title: "Character Gallery",
        characters_subtitle: "Explore all Attack on Titan characters featured in AOTdle",
        characters_count: "characters",
        filter: "Filter",
        clear: "Clear",
        all: "All",

        // Guide Page
        guide_title: "How to Play",
        guide_subtitle: "Master AOTdle with this comprehensive guide",
        guide_objective: "Objective",
        guide_objective_text: "Guess the hidden Attack on Titan character of the day. Each day at midnight, a new character is selected for each game mode. You have unlimited guesses, but the goal is to identify the character in as few attempts as possible.",
        guide_modes: "Game Modes",
        guide_classic_text: "Guess the character using attribute clues. After each guess, you'll see colored feedback indicating how close your guess is to the target character.",
        guide_quote_text: "Identify who said the famous quote. A memorable line from the series is displayed, and you must guess which character spoke it.",
        guide_image_text: "Recognize the character from their image. The image starts blurred and becomes clearer as you make more guesses.",
        guide_colors: "Color Indicators",
        guide_green: "The attribute matches exactly with the target character.",
        guide_yellow: "Close but not exact. For arcs, this means an adjacent story arc.",
        guide_gray: "The attribute does not match the target character.",
        guide_arrows: "Arrow Indicators",
        guide_arrow_up: "Arrow Up",
        guide_arrow_up_text: "The target character appears in a later arc (higher number).",
        guide_arrow_down: "Arrow Down",
        guide_arrow_down_text: "The target character appears in an earlier arc (lower number).",
        guide_hints: "Hint System",
        guide_hints_text: "As you make more guesses, hints about the target character are gradually revealed. After 2 guesses, you'll see the arc. After 4, the affiliation. After 6, the species. And after 8 guesses, the status is revealed.",
        guide_tips: "Pro Tips",
        guide_tip_1: "Start with popular characters to quickly narrow down the possibilities.",
        guide_tip_2: "Pay attention to arc arrows - they tell you which direction to look in the timeline.",
        guide_tip_3: "Use the hint boxes strategically - they unlock valuable information as you guess.",
        guide_tip_4: "Check the Character Gallery to familiarize yourself with all available characters.",

        // Trivia Page
        trivia_title: "Attack on Titan Trivia",
        trivia_subtitle: "Discover interesting facts about the world of Attack on Titan",
        trivia_arcs: "Story Arcs Timeline",
        trivia_facts: "Did You Know?",
        trivia_titans: "The Nine Titans",
        fact_1: "Attack on Titan manga ran from 2009 to 2021 with 139 chapters.",
        fact_2: "The series features over 100 named characters.",
        fact_3: "Levi Ackerman is considered humanity's strongest soldier.",
        fact_4: "There are 9 Titan Shifters in total.",
        fact_5: "The anime adaptation began in 2013 and concluded in 2023.",
        fact_6: "The Ackerman clan possesses extraordinary combat abilities.",

        // Spoiler System
        spoiler_warning: "Spoiler Warning",
        spoiler_warning_text: "Character status and species may contain story spoilers",
        spoiler_hidden: "Spoiler hidden",
        show_spoilers: "Show",
        hide_spoilers: "Hide",
        major_spoilers: "Major Spoilers",
        trivia_spoiler_text: "Titan holders and story details contain major spoilers",
        titans_spoiler_text: "This section reveals the identities of all Titan Shifters. Enable spoilers to view.",

        // SEO & Landing Content
        seo_about_title: "About AOTdle",
        seo_about_text_1: "AOTdle is the ultimate daily trivia game for Attack on Titan (Shingeki no Kyojin) fans. Test your knowledge of the Scout Regiment, Titans, and the world of Paradis Island. Every day brings a new challenge across three distinct game modes.",
        seo_about_text_2: "Whether you're guessing characters by their attributes like Arc, Affiliation, and Species, identifying famous quotes, or recognizing characters from blurred images, AOTdle offers endless entertainment for anime enthusiasts.",
        seo_features_title: "Game Features",
        seo_feature_daily: "Daily Refresh: New characters, quotes, and images every midnight.",
        seo_feature_hints: "Intelligent Hints: Unlock clues about Arcs, Regiments, and more as you guess.",
        seo_feature_spoiler: "Spoiler Safety: Advanced spoiler protection for anime-only fans.",
        seo_feature_langs: "Multi-language: Play in English, Turkish, or Spanish."
    },
    tr: {
        // Header & Navigation
        settings: "Ayarlar",
        stats: "İstatistikler",
        help: "Yardım",
        home_subtitle: "Titan Karakterlerini Tahmin Et",
        choose_challenge: "Mücadeleni Seç",

        // Game Modes
        classic: "Klasik",
        classic_desc: "Özelliklerden karakteri tahmin et",
        quote: "Replik",
        quote_desc: "Bu sözü kim söyledi?",
        image: "Görsel",
        image_desc: "Karakteri tanı",

        // Game Interface
        search_placeholder: "Karakter ara...",
        archive_closed: "Anket Tamamlandı",
        record: "Titan Kaydı",
        discovery: "Kimlik Doğrulandı",
        it_was: "O kişi:",

        // Column Headers
        col_character: "Karakter",
        col_gender: "Cinsiyet",
        col_arc: "Bölüm",
        col_affiliation: "Birlik",
        col_species: "Tür",
        col_height: "Boy",
        col_status: "Durum",

        // Stats
        records: "Kayıtlar",
        played: "Oyun",
        win_rate: "Kazanma",
        streak: "Seri",
        next_entry: "Sonraki Karakter",

        // Help Section
        guide: "Keşif Birliği Rehberi",
        how_to: "Nasıl Oynanır",
        help_intro: "Gizli Attack on Titan karakterini bul.",
        indicators: "Göstergeler",
        exact_match: "Tam Eşleşme",
        partial_match: "Kısmi Eşleşme (komşu bölüm)",
        incorrect: "Yanlış",
        chronology: "Kronoloji",
        chronology_desc: "Oklar, karakterin daha erken (↓) veya daha geç (↑) bir bölümde görünüp görünmediğini gösterir.",
        language: "Dil",
        hint: "İpucu",
        unlocks_in: "Kalan:",
        yesterday_character: "Dünkü karakter #{number} {name} idi",

        // Victory & Share
        share_results: "Paylaş",
        share_twitter: "X / Twitter",
        share_whatsapp: "WhatsApp",
        copied: "Kopyalandı!",
        victory_heading: "Kalbini Adamaya!",
        next_character: "Sonraki Karakter",

        // Arc Names
        arc_Fall_of_Shiganshina: "Shiganshina'nın Düşüşü",
        arc_Training: "Eğitim",
        arc_Trost: "Trost",
        arc_Female_Titan: "Dişi Titan",
        arc_Clash_of_Titans: "Titanların Çatışması",
        arc_Uprising: "Ayaklanma",
        arc_Return_to_Shiganshina: "Shiganshina'ya Dönüş",
        arc_Marley: "Marley",
        arc_War_for_Paradis: "Paradis Savaşı",

        // Rules
        rule_classic_header: "Klasik Mod",
        rule_classic_text: "İpuçlarını takip et:",
        rule_classic_gender: "Cinsiyet: Tam eşleşmeli.",
        rule_classic_arc: "Bölüm: Tam ise Yeşil. Yakınsa Sarı. Oklar (↑/↓) zaman yönünü gösterir.",
        rule_classic_affiliation: "Birlik: Tam eşleşmeli.",
        rule_classic_species: "Tür: Tam eşleşmeli.",
        rule_classic_height: "Boy: Tam eşleşmeli.",
        rule_classic_status: "Durum: Tam eşleşmeli.",
        rule_quote_header: "Replik Modu",
        rule_quote_text: "Sözün sahibini bul.",
        rule_image_header: "Görsel Modu",
        rule_image_text: "Görseldeki karakteri tanı.",
        rule_guesses: "Sınırsız tahmin hakkı.",

        // Footer & Legal
        footer_privacy: "Gizlilik Politikası",
        footer_about: "Hakkında",
        footer_contact: "İletişim",
        back_to_game: "Ana Sayfaya Dön",

        // Privacy Page
        privacy_title: "Gizlilik Politikası",
        privacy_intro: "AOTdle'da gizliliğinize önem veriyoruz. Bu politika bilgileri nasıl ele aldığımızı açıklar.",
        privacy_point_1: "Kişisel Veri Toplanmaz: Kullanıcı hesabı veya giriş gerekmez. Oynarken tamamen anonimsiniz.",
        privacy_point_2: "Çerezler ve Onay: Dil, günlük ilerleme ve onay tercihleri için LocalStorage kullanıyoruz. Google Analytics kullanmıyoruz.",
        privacy_point_3: "Reklamlar: Google AdSense kullanıyoruz. Reklam ayarları, onay seçiminize göre Google Consent Mode ile yönetilir.",

        // About Page
        about_title: "AOTdle Hakkında",
        about_text: "AOTdle, Attack on Titan hayranları için günlük tahmin oyunudur. Wordle'dan esinlenerek, AOT bilginizi eğlenceli bir şekilde test etmenizi sağlıyoruz. Her gece yarısı yeni görevler yayınlanır. Bu bir hayran projesidir ve Attack on Titan'ın yaratıcıları ile herhangi bir bağlantısı yoktur.",

        // Contact Page
        contact_title: "İletişim",
        contact_text: "Bir öneriniz veya hata mı buldunuz?",
        contact_email: "Bize ulaşın: hello@aotdle.com",

        // Settings
        setting_high_contrast: "Yüksek Kontrast",
        setting_high_contrast_desc: "Renk görünürlüğünü artırır.",
        setting_reset: "Verileri Sıfırla",
        setting_reset_desc: "İstatistikleri ve geçmişi siler.",
        setting_reset_confirm: "Emin misiniz?",
        feedback: "Geri Bildirim",

        // About Page extended
        about_features: "Oyun Özellikleri",
        about_feature_1: "67+ Attack on Titan karakteri",
        about_feature_2: "3 oyun modu: Klasik, Replik ve Görsel",
        about_feature_3: "İngilizce, Türkçe ve İspanyolca dillerinde",
        about_feature_4: "Her gece yarısı yeni görevler",
        about_feature_5: "Sonuçlarını arkadaşlarınla paylaş",
        about_credits: "Teşekkürler",
        about_credits_text: "Attack on Titan (Shingeki no Kyojin) Hajime Isayama tarafından yaratılmıştır. Bu sadece eğlence amaçlı hayran yapımı bir projedir. Tüm karakter görselleri ve isimleri sahiplerinin mülkiyetindedir.",

        // Contact Page extended
        send_email: "E-posta Gönder",
        follow_us: "Bizi Takip Edin",

        // Settings extended
        reset_button: "Sıfırla",
        max_streak: "Maks",

        // Value translations - Affiliations
        affiliation_Scout_Regiment: "Keşif Birliği",
        affiliation_Garrison: "Garnizon",
        affiliation_Military_Police: "Askeri Polis",
        affiliation_Warriors: "Savaşçılar",
        affiliation_Yeagerists: "Yeagerciler",
        affiliation_Civilians: "Siviller",
        affiliation_Marleyan_Military: "Marley Ordusu",

        // Value translations - Species
        species_Human: "İnsan",
        species_Titan_Shifter: "Titan Dönüşümcü",

        // Value translations - Genders
        gender_Male: "Erkek",
        gender_Female: "Kadın",

        // Value translations - Height
        height_Short: "Kısa",
        height_Average: "Orta",
        height_Tall: "Uzun",

        // Value translations - Status
        status_Alive: "Hayatta",
        status_Deceased: "Vefat Etmiş",

        // Cookie Consent
        cookie_title: "Çerez Bildirimi",
        cookie_message: "Temel özellikler ve AdSense reklam ayarları için depolama ve onay sinyallerini kullanıyoruz. Reklamla ilgili onayı kabul edebilir veya reddedebilirsiniz.",
        cookie_accept: "Tümünü Kabul Et",
        cookie_decline: "Reddet",

        // Landing Page Stats
        landing_subtitle: "Attack on Titan bilgini test et",
        stat_characters: "67+ Karakter",
        stat_arcs: "9 Hikaye Bölümü",
        stat_daily: "Günlük Görevler",
        footer_disclaimer: "Hayran yapımı • Attack on Titan ile bağlantılı değildir",
        footer_copyright: "© {year} AOTdle • Hayran yapımı proje • Attack on Titan ile bağlantılı değildir",
        footer_description: "AOTdle, Attack on Titan hayranları için ücretsiz bir günlük tahmin oyunudur. İpuçları, replikler ve görsellerden karakterleri tanımlayarak bilginizi test edin. Her gece yarısı yeni görevler.",

        // Autocomplete
        no_results: "Karakter bulunamadı",
        results_found: "karakter bulundu",
        character_results: "Karakter arama sonuçları",
        search_instructions: "Karakter aramak için yazın. Sonuçlar arasında ok tuşlarıyla gezinin, seçmek için Enter'a basın.",

        // Share
        guess_singular: "tahmin",
        guess_plural: "tahmin",

        // Navigation
        nav_characters: "Karakterler",
        nav_guide: "Nasıl Oynanır",
        nav_trivia: "Bilgiler",

        // Character Gallery
        characters_title: "Karakter Galerisi",
        characters_subtitle: "AOTdle'daki tüm Attack on Titan karakterlerini keşfedin",
        characters_count: "karakter",
        filter: "Filtrele",
        clear: "Temizle",
        all: "Tümü",

        // Guide Page
        guide_title: "Nasıl Oynanır",
        guide_subtitle: "Bu kapsamlı rehberle AOTdle'da ustalık kazanın",
        guide_objective: "Amaç",
        guide_objective_text: "Günün gizli Attack on Titan karakterini tahmin edin. Her gece yarısı her oyun modu için yeni bir karakter seçilir. Sınırsız tahmin hakkınız var, ama hedef karakteri mümkün olduğunca az denemede bulmaktır.",
        guide_modes: "Oyun Modları",
        guide_classic_text: "Özellik ipuçlarını kullanarak karakteri tahmin edin. Her tahminden sonra, tahmininizin hedef karaktere ne kadar yakın olduğunu gösteren renkli geri bildirim göreceksiniz.",
        guide_quote_text: "Ünlü repliklerin kime ait olduğunu tahmin edin. Diziden akılda kalan bir söz gösterilir ve hangi karakterin söylediğini bilmeniz gerekir.",
        guide_image_text: "Görselden karakteri tanıyın. Görsel bulanık başlar ve daha fazla tahmin yaptıkça netlesir.",
        guide_colors: "Renk Göstergeleri",
        guide_green: "Özellik hedef karakterle tam olarak eşleşiyor.",
        guide_yellow: "Yakın ama tam değil. Arc'lar için bu, komşu bir hikaye bölümü anlamına gelir.",
        guide_gray: "Özellik hedef karakterle eşlesmedi.",
        guide_arrows: "Ok Göstergeleri",
        guide_arrow_up: "Yukarı Ok",
        guide_arrow_up_text: "Hedef karakter daha sonraki bir arc'ta görünüyor (daha yüksek numara).",
        guide_arrow_down: "Aşağı Ok",
        guide_arrow_down_text: "Hedef karakter daha önceki bir arc'ta görünüyor (daha düşük numara).",
        guide_hints: "İpucu Sistemi",
        guide_hints_text: "Daha fazla tahmin yaptıkça, hedef karakter hakkında ipuçları kademeli olarak açılır. 2 tahminden sonra arc'i, 4'ten sonra birliği, 6'dan sonra türü ve 8 tahminden sonra durumu göreceksiniz.",
        guide_tips: "Profesyonel İpuçları",
        guide_tip_1: "Olasılıkları hızla daraltmak için popüler karakterlerle başlayın.",
        guide_tip_2: "Arc oklarına dikkat edin - zaman çizelgesinde hangi yöne bakmanız gerektiğini söylüyorlar.",
        guide_tip_3: "İpucu kutularını stratejik olarak kullanın - tahmin ettikçe değerli bilgiler açılır.",
        guide_tip_4: "Tüm mevcut karakterleri tanımak için Karakter Galerisi'ni inceleyin.",

        // Trivia Page
        trivia_title: "Attack on Titan Bilgileri",
        trivia_subtitle: "Attack on Titan dünyası hakkında ilginç gerçekleri keşfedin",
        trivia_arcs: "Hikaye Bölümleri Zaman Çizelgesi",
        trivia_facts: "Biliyor muydunuz?",
        trivia_titans: "Dokuz Titan",
        fact_1: "Attack on Titan mangası 2009'dan 2021'e kadar 139 bölümle yayınlandı.",
        fact_2: "Dizide 100'den fazla isimli karakter bulunuyor.",
        fact_3: "Levi Ackerman, insanlığın en güçlü askeri olarak kabul edilir.",
        fact_4: "Toplamda 9 Titan Dönüşümcü vardır.",
        fact_5: "Anime uyarlaması 2013'te başladı ve 2023'te sona erdi.",
        fact_6: "Ackerman klanı olağanüstü savaş yeteneklerine sahiptir.",

        // Spoiler System
        spoiler_warning: "Spoiler Uyarısı",
        spoiler_warning_text: "Karakter durumu ve türü hikaye spoiler'ı içerebilir",
        spoiler_hidden: "Spoiler gizli",
        show_spoilers: "Göster",
        hide_spoilers: "Gizle",
        major_spoilers: "Büyük Spoilerlar",
        trivia_spoiler_text: "Titan sahipleri ve hikaye detayları büyük spoilerlar içerir",
        titans_spoiler_text: "Bu bölüm tüm Titan Dönüşümcülerin kimliklerini açıklar. Görmek için spoilerları etkinleştirin.",

        // SEO & Landing Content
        seo_about_title: "AOTdle Hakkında",
        seo_about_text_1: "AOTdle, Attack on Titan (Shingeki no Kyojin) hayranları için hazırlanan en kapsamlı günlük tahmin oyunudur. Keşif Birliği, Titanlar ve Paradis Adası dünyasına dair bilginizi test edin. Her gün üç farklı oyun modunda yeni bir mücadele sizi bekliyor.",
        seo_about_text_2: "Karakterleri Bölüm, Birlik ve Tür gibi özelliklerinden tahmin edin, ünlü replikleri tanıyın veya bulanık görsellerden kim olduklarını bulun. AOTdle, anime tutkunları için sonsuz eğlence sunuyor.",
        seo_features_title: "Oyun Özellikleri",
        seo_feature_daily: "Günlük Yenilenme: Her gece yarısı yeni karakterler, replikler ve görseller.",
        seo_feature_hints: "Akıllı İpuçları: Tahmin yaptıkça Bölümler, Birlikler ve daha fazlası hakkında ipuçları açılır.",
        seo_feature_spoiler: "Spoiler Koruması: Anime izleyicileri için gelişmiş spoiler güvenliği.",
        seo_feature_langs: "Çoklu Dil Desteği: İngilizce, Türkçe veya İspanyolca oynayın."
    },
    es: {
        // Header & Navigation
        settings: "Ajustes",
        stats: "Estadísticas",
        help: "Ayuda",
        home_subtitle: "Adivina los Personajes Titán",
        choose_challenge: "Elige Tu Desafío",

        // Game Modes
        classic: "Clásico",
        classic_desc: "Adivina el personaje por atributos",
        quote: "Cita",
        quote_desc: "Identifica quién lo dijo",
        image: "Imagen",
        image_desc: "Reconoce al personaje",

        // Game Interface
        search_placeholder: "Buscar un personaje...",
        archive_closed: "Encuesta Completada",
        record: "Registro Titán",
        discovery: "Identidad Confirmada",
        it_was: "Era",

        // Column Headers
        col_character: "Personaje",
        col_gender: "Género",
        col_arc: "Arco",
        col_affiliation: "Regimiento",
        col_species: "Especie",
        col_height: "Altura",
        col_status: "Estado",

        // Stats
        records: "Registros",
        played: "Jugados",
        win_rate: "Victorias",
        streak: "Racha",
        next_entry: "Siguiente Personaje",

        // Help Section
        guide: "Guía del Cuerpo de Exploración",
        how_to: "Cómo Jugar",
        help_intro: "Identifica al personaje oculto de Attack on Titan.",
        indicators: "Indicadores",
        exact_match: "Coincidencia Exacta",
        partial_match: "Coincidencia Parcial (arco adyacente)",
        incorrect: "Incorrecto",
        chronology: "Cronología",
        chronology_desc: "Las flechas indican si el personaje apareció en un arco anterior (↓) o posterior (↑).",
        language: "Idioma",
        hint: "Pista",
        unlocks_in: "Desbloquea en",
        yesterday_character: "El personaje de ayer #{number} fue {name}",

        // Victory & Share
        share_results: "Compartir Resultados",
        share_twitter: "X / Twitter",
        share_whatsapp: "WhatsApp",
        copied: "¡Copiado!",
        victory_heading: "¡Shinzou wo Sasageyo!",
        next_character: "Siguiente Personaje",

        // Arc Names
        arc_Fall_of_Shiganshina: "Caída de Shiganshina",
        arc_Training: "Entrenamiento",
        arc_Trost: "Trost",
        arc_Female_Titan: "Titán Hembra",
        arc_Clash_of_Titans: "Choque de Titanes",
        arc_Uprising: "Insurrección",
        arc_Return_to_Shiganshina: "Regreso a Shiganshina",
        arc_Marley: "Marley",
        arc_War_for_Paradis: "Guerra por Paradis",

        // Rules
        rule_classic_header: "Modo Clásico",
        rule_classic_text: "Adivina el personaje por atributos:",
        rule_classic_gender: "Género: Coincidencia exacta.",
        rule_classic_arc: "Arco: Verde si es exacto. Amarillo si es adyacente. Flechas (↑/↓) muestran dirección.",
        rule_classic_affiliation: "Regimiento: Coincidencia exacta.",
        rule_classic_species: "Especie: Coincidencia exacta.",
        rule_classic_height: "Altura: Coincidencia exacta.",
        rule_classic_status: "Estado: Coincidencia exacta.",
        rule_quote_header: "Modo Cita",
        rule_quote_text: "Identifica quién dijo la cita.",
        rule_image_header: "Modo Imagen",
        rule_image_text: "Identifica al personaje de la imagen.",
        rule_guesses: "Tienes intentos ilimitados.",

        // Footer & Legal
        footer_privacy: "Política de Privacidad",
        footer_about: "Acerca de",
        footer_contact: "Contacto",
        back_to_game: "Volver al Juego",

        // Privacy Page
        privacy_title: "Política de Privacidad",
        privacy_intro: "En AOTdle, priorizamos tu privacidad. Esta política describe cómo manejamos la información.",
        privacy_point_1: "Sin Recolección de Datos Personales: No requerimos cuentas, inicios de sesión ni emails. Eres completamente anónimo mientras juegas.",
        privacy_point_2: "Cookies y Consentimiento: Usamos LocalStorage para idioma, progreso diario y preferencias de consentimiento. No usamos Google Analytics.",
        privacy_point_3: "Publicidad: Usamos Google AdSense. La configuración de anuncios se gestiona con Google Consent Mode según tu elección de consentimiento.",

        // About Page
        about_title: "Acerca de AOTdle",
        about_text: "AOTdle es un juego de adivinanzas diario para fans de Attack on Titan. Inspirado en Wordle, nuestro objetivo es hacer que probar tu conocimiento de AOT sea divertido. Nuevos desafíos se publican cada día a medianoche. Este es un proyecto de fans y no está afiliado con los creadores de Attack on Titan.",

        // Contact Page
        contact_title: "Contáctanos",
        contact_text: "¿Tienes una sugerencia o encontraste un error?",
        contact_email: "Escríbenos a: hello@aotdle.com",

        // Settings
        setting_high_contrast: "Modo Alto Contraste",
        setting_high_contrast_desc: "Aumenta la visibilidad de colores.",
        setting_reset: "Restablecer Datos",
        setting_reset_desc: "Borra estadísticas e historial.",
        setting_reset_confirm: "¿Estás seguro?",
        feedback: "Comentarios",

        // About Page extended
        about_features: "Características del Juego",
        about_feature_1: "67+ personajes de Attack on Titan",
        about_feature_2: "3 modos de juego: Clásico, Cita e Imagen",
        about_feature_3: "Disponible en inglés, turco y español",
        about_feature_4: "Nuevos desafíos cada día a medianoche",
        about_feature_5: "Comparte tus resultados con amigos",
        about_credits: "Créditos",
        about_credits_text: "Attack on Titan (Shingeki no Kyojin) fue creado por Hajime Isayama. Este es un proyecto de fans solo para entretenimiento. Todas las imágenes y nombres de personajes son propiedad de sus respectivos dueños.",

        // Contact Page extended
        send_email: "Enviar Email",
        follow_us: "Síguenos",

        // Settings extended
        reset_button: "Restablecer",
        max_streak: "Máx",

        // Value translations - Affiliations
        affiliation_Scout_Regiment: "Cuerpo de Exploración",
        affiliation_Garrison: "Guarnición",
        affiliation_Military_Police: "Policía Militar",
        affiliation_Warriors: "Guerreros",
        affiliation_Yeagerists: "Yeageristas",
        affiliation_Civilians: "Civiles",
        affiliation_Marleyan_Military: "Ejército Marleyano",

        // Value translations - Species
        species_Human: "Humano",
        species_Titan_Shifter: "Cambiante Titán",

        // Value translations - Genders
        gender_Male: "Masculino",
        gender_Female: "Femenino",

        // Value translations - Height
        height_Short: "Bajo",
        height_Average: "Promedio",
        height_Tall: "Alto",

        // Value translations - Status
        status_Alive: "Vivo",
        status_Deceased: "Fallecido",

        // Cookie Consent
        cookie_title: "Aviso de Cookies",
        cookie_message: "Usamos almacenamiento y señales de consentimiento para funciones principales y configuración de anuncios de AdSense. Puedes aceptar o rechazar el consentimiento de anuncios.",
        cookie_accept: "Aceptar Todo",
        cookie_decline: "Rechazar",

        // Landing Page Stats
        landing_subtitle: "Pon a prueba tu conocimiento de Attack on Titan",
        stat_characters: "67+ Personajes",
        stat_arcs: "9 Arcos Narrativos",
        stat_daily: "Desafíos Diarios",
        footer_disclaimer: "Hecho por fans • No afiliado con Attack on Titan",
        footer_copyright: "© {year} AOTdle • Proyecto de fans • No afiliado con Attack on Titan",
        footer_description: "AOTdle es un juego de adivinanzas diario gratuito para fans de Attack on Titan. Pon a prueba tu conocimiento identificando personajes por pistas, citas e imágenes. Nuevos desafíos cada día a medianoche.",

        // Autocomplete
        no_results: "No se encontraron personajes",
        results_found: "personajes encontrados",
        character_results: "Resultados de búsqueda de personajes",
        search_instructions: "Escribe para buscar personajes. Usa las flechas para navegar, Enter para seleccionar.",

        // Share
        guess_singular: "intento",
        guess_plural: "intentos",

        // Navigation
        nav_characters: "Personajes",
        nav_guide: "Cómo Jugar",
        nav_trivia: "Curiosidades",

        // Character Gallery
        characters_title: "Galería de Personajes",
        characters_subtitle: "Explora todos los personajes de Attack on Titan en AOTdle",
        characters_count: "personajes",
        filter: "Filtrar",
        clear: "Limpiar",
        all: "Todos",

        // Guide Page
        guide_title: "Cómo Jugar",
        guide_subtitle: "Domina AOTdle con esta guía completa",
        guide_objective: "Objetivo",
        guide_objective_text: "Adivina el personaje oculto de Attack on Titan del día. Cada día a medianoche, se selecciona un nuevo personaje para cada modo. Tienes intentos ilimitados, pero el objetivo es identificar al personaje en la menor cantidad de intentos.",
        guide_modes: "Modos de Juego",
        guide_classic_text: "Adivina el personaje usando pistas de atributos. Después de cada intento, verás comentarios de colores que indican qué tan cerca está tu intento del personaje objetivo.",
        guide_quote_text: "Identifica quién dijo la cita famosa. Se muestra una línea memorable de la serie, y debes adivinar qué personaje la dijo.",
        guide_image_text: "Reconoce al personaje por su imagen. La imagen comienza borrosa y se vuelve más clara a medida que haces más intentos.",
        guide_colors: "Indicadores de Color",
        guide_green: "El atributo coincide exactamente con el personaje objetivo.",
        guide_yellow: "Cercano pero no exacto. Para arcos, significa un arco de historia adyacente.",
        guide_gray: "El atributo no coincide con el personaje objetivo.",
        guide_arrows: "Indicadores de Flecha",
        guide_arrow_up: "Flecha Arriba",
        guide_arrow_up_text: "El personaje objetivo aparece en un arco posterior (número más alto).",
        guide_arrow_down: "Flecha Abajo",
        guide_arrow_down_text: "El personaje objetivo aparece en un arco anterior (número más bajo).",
        guide_hints: "Sistema de Pistas",
        guide_hints_text: "A medida que haces más intentos, se revelan pistas sobre el personaje objetivo. Después de 2 intentos verás el arco. Después de 4, la afiliación. Después de 6, la especie. Y después de 8, el estado.",
        guide_tips: "Consejos Pro",
        guide_tip_1: "Comienza con personajes populares para reducir rápidamente las posibilidades.",
        guide_tip_2: "Presta atención a las flechas de arco - te dicen en qué dirección mirar en la línea de tiempo.",
        guide_tip_3: "Usa las cajas de pistas estratégicamente - desbloquean información valiosa mientras adivinas.",
        guide_tip_4: "Consulta la Galería de Personajes para familiarizarte con todos los personajes disponibles.",

        // Trivia Page
        trivia_title: "Curiosidades de Attack on Titan",
        trivia_subtitle: "Descubre datos interesantes sobre el mundo de Attack on Titan",
        trivia_arcs: "Línea de Tiempo de Arcos",
        trivia_facts: "¿Sabías que?",
        trivia_titans: "Los Nueve Titanes",
        fact_1: "El manga de Attack on Titan se publicó de 2009 a 2021 con 139 capítulos.",
        fact_2: "La serie cuenta con más de 100 personajes con nombre.",
        fact_3: "Levi Ackerman es considerado el soldado más fuerte de la humanidad.",
        fact_4: "Hay 9 Cambiantes Titán en total.",
        fact_5: "La adaptación al anime comenzó en 2013 y concluyó en 2023.",
        fact_6: "El clan Ackerman posee habilidades de combate extraordinarias.",

        // Spoiler System
        spoiler_warning: "Advertencia de Spoilers",
        spoiler_warning_text: "El estado y especie de los personajes pueden contener spoilers",
        spoiler_hidden: "Spoiler oculto",
        show_spoilers: "Mostrar",
        hide_spoilers: "Ocultar",
        major_spoilers: "Spoilers Importantes",
        trivia_spoiler_text: "Los portadores de Titanes y detalles de la historia contienen spoilers importantes",
        titans_spoiler_text: "Esta sección revela las identidades de todos los Cambiantes Titán. Activa los spoilers para ver.",

        // SEO & Landing Content
        seo_about_title: "Acerca de AOTdle",
        seo_about_text_1: "AOTdle es el juego de trivia diario definitivo para los fans de Attack on Titan (Shingeki no Kyojin). Pon a prueba tu conocimiento sobre el Cuerpo de Exploración, los Titanes y el mundo de la Isla Paradis. Cada día trae un nuevo desafío en tres modos de juego distintos.",
        seo_about_text_2: "Ya sea adivinando personajes por sus atributos como Arco, Afiliación y Especie, identificando citas famosas o reconociendo personajes a partir de imágenes borrosas, AOTdle ofrece entretenimiento sin fin para los entusiastas del anime.",
        seo_features_title: "Características del Juego",
        seo_feature_daily: "Actualización Diaria: Nuevos personajes, citas e imágenes cada medianoche.",
        seo_feature_hints: "Pistas Inteligentes: Desbloquea pistas sobre Arcos, Regimientos y más mientras adivinas.",
        seo_feature_spoiler: "Seguridad de Spoilers: Protección avanzada contra spoilers para fans que solo ven el anime.",
        seo_feature_langs: "Multilenguaje: Juega en inglés, turco o español."
    }
};
